'use client';

import {
  createContext,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useState,
} from 'react';
import type {
  Firm,
  FirmCustomStatus,
  FirmGroup,
  FirmThemeBySlug,
  FirmPreferences,
  LinkedAccount,
} from '@/_types/models/firm';
import type { FirmSettingsProviderProps } from './FirmSettingsProvider.types';
import type { FirmUser } from '@/_types/models/user';
import { checkStripeIntegration } from '@/_api/admin-stripe';
import { getFirmRoles } from '@/_api/admin-firm-roles';
import { DEFAULT_FIRM_PREFERENCES, EMPTY_TAG_STRING } from '@/_constants/firm';
import { getCurrentFirm, getAvailableFirms } from '@/_api/client-firms';
import {
  getFirmUsers,
  getFirmCustomStatuses,
  updatePreferences,
  getLinkedAccounts,
  initializeCustomPermissionLevels,
  resetPermissionLevelsToDefaults,
  updateCustomPermissionLevel,
} from '@/_api/admin-firms';
import { getFullName } from '@/_utils/user';
import tinycolor from 'tinycolor2';
import { ResultListItem, type SelectItem } from '@/_components';
import { usePathname } from 'next/navigation';
import { UserContext } from '../UserProvider';
import { getFirmThemeBySlug } from '@/_api/firm';
import { FirmRole } from '@/_types/models/firmRole';
import { ToastContext } from '../ToastProvider';
import { getGlossaries } from './FirmSettingsProvider.utils';
import { getAllEntityTypes } from '@/_api/admin-companies';
import { PermissionsMapEnum } from '@/_types/permissions';
import { FIRM_ACCESS_OPTIONS } from '@/_constants/roles';
import { getDeliveryMessageTemplates } from '@/_api/admin-tax-returns';
import type { DeliveryMessageTemplate } from '@/_types/taxReturnDelivery';

const MOCK_FIRMS: Array<Pick<Firm, 'id' | 'name'>> = [
  {
    id: 4,
    name: 'The tax preparations company in Arizona, LLC',
  },
];

const FirmSettingsContext = createContext<FirmSettingsProviderProps>({
  availableFirms: [],
  clientTagOptions: [],
  clientTagOptionsToAdd: [],
  deliveryMessageTemplates: [],
  entityTagOptions: [],
  entityTagOptionsToAdd: [],
  firm: undefined,
  firmCustomStatuses: [],
  firmEntityTypes: [],
  firmGroupOptions: [],
  firmGroupQuestionnaireOptions: [],
  firmGroups: [],
  firmLinkedAccounts: [],
  firmLogoUrl: undefined,
  firmRoleOptions: [],
  firmRoles: [],
  firmTheme: undefined,
  firmUserOptions: [],
  firmUsers: [],
  glossaries: {},
  hasCustomPermissionLevels: false,
  initializeCustomPermissionLevels: () => Promise.resolve(),
  isLoading: false,
  isLoadingDeliveryMessageTemplates: false,
  isLoadingFirmPreferences: true,
  isLoadingPermissionLevels: false,
  isStripeConnected: false,
  permissionLevelsConfig: undefined,
  preferences: DEFAULT_FIRM_PREFERENCES,
  reloadDeliveryMessageTemplates: () => Promise.resolve(),
  reloadFirm: () => Promise.resolve(),
  reloadFirmRoles: () => null,
  reloadFirmUsers: () => null,
  resetPermissionLevelsToDefaults: () => Promise.resolve(),
  setFirm: () => null,
  setFirmTheme: () => null,
  updateCustomPermissionLevel: () => Promise.resolve(),
  updateFirmPreference: () => null,
  updateFirmPreferences: () => Promise.resolve(),
  firmAccessOptions: [],
});

const FirmSettingsProvider: any = ({ children }: any) => {
  const [availableFirms, setAvailableFirms] = useState<
    Array<Pick<Firm, 'id' | 'name'>>
  >([]);
  const [firm, setFirm] = useState<Firm | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isLoadingFirmPreferences, setLoadingFirmPreferences] =
    useState<boolean>(true);
  const [firmCustomStatuses, setFirmCustomStatuses] = useState<
    Array<FirmCustomStatus>
  >([]);
  const [firmTheme, setFirmTheme] = useState<
    FirmThemeBySlug | undefined | null
  >(undefined);
  const [firmRoles, setFirmRoles] = useState<Array<FirmRole>>([]);
  const [firmUsers, setFirmUsers] = useState<Array<FirmUser>>([]);
  const [firmLinkedAccounts, setFirmLinkedAccounts] = useState<
    Array<LinkedAccount>
  >([]);
  const [firmEntityTypes, setFirmEntityTypes] = useState<Array<string>>([]);
  const [isStripeConnected, setStripeConnected] = useState<boolean>(false);
  const [isLoadingPermissionLevels, setLoadingPermissionLevels] =
    useState<boolean>(false);
  const [deliveryMessageTemplates, setDeliveryMessageTemplates] = useState<
    Array<DeliveryMessageTemplate>
  >([]);
  const [
    isLoadingDeliveryMessageTemplates,
    setLoadingDeliveryMessageTemplates,
  ] = useState<boolean>(false);

  const pathname = usePathname();
  const { token, isAdvisor, user } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const glossaries: Record<string, string> | undefined = getGlossaries(
    firm?.firm_vocabs,
  );

  const loadFirmRoles = useCallback(async () => {
    if (!token || !isAdvisor) return;

    try {
      setLoading(true);

      const newFirmRoles = await getFirmRoles({ token });

      if (!newFirmRoles?.error) {
        setFirmRoles(newFirmRoles);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, isAdvisor]);

  const loadAdvisors = useCallback(async () => {
    if (!isAdvisor || !token) return;
    try {
      setLoading(true);
      const response = await getFirmUsers({ token });

      if (!response) throw new Error('Failed to get firm users');

      const { users: fUsers } = response;

      setFirmUsers(fUsers);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, isAdvisor]);

  const reloadFirm = useCallback(async () => {
    if (!token) return;

    try {
      const _loadCurrentFirm = async () => {
        const firm = await getCurrentFirm({ token });
        setFirm(firm);
        setLoadingFirmPreferences(false);
      };

      const _loadAvailableFirms = async () => {
        const firms = await getAvailableFirms({ token });

        if (!!firms?.error) {
          return setAvailableFirms([]);
        }

        const allFirms: Array<Pick<Firm, 'id' | 'name'>> = firms.map(
          ({ id, name }) => ({
            id,
            name,
          }),
        );

        setAvailableFirms([...allFirms, ...MOCK_FIRMS]);
      };

      const _getCheckStripeConnection = async () => {
        if (!isAdvisor || !token) return;
        const response = await checkStripeIntegration({ token });

        if (!response) throw new Error('Failed to check Stripe connection');

        const { connected } = response;

        setStripeConnected(!!connected);
      };

      _loadCurrentFirm();
      _loadAvailableFirms();

      loadFirmRoles();
      loadAdvisors();

      _getCheckStripeConnection();
    } catch (error) {
      console.error({ error });
    } finally {
      setLoading(false);
    }
  }, [isAdvisor, loadFirmRoles, loadAdvisors, token]);

  const firmPreferences: FirmPreferences = useMemo(() => {
    return {
      ...DEFAULT_FIRM_PREFERENCES,
      ...(firm?.preferences || {}),
    };
  }, [firm?.preferences]);

  const loadFirmTheme = useCallback(async () => {
    if (typeof window === 'undefined') return;

    const currentUrl = new URL(window.location.href);
    const pathSegments: Array<string> =
      currentUrl.pathname.split('/').filter(Boolean) || [];
    const alias: string = currentUrl.hostname;
    const slug: string | undefined = pathSegments[0];

    if (!slug && ['app.soraban.com', 'localhost'].includes(alias)) {
      return;
    }

    const theme = await getFirmThemeBySlug({
      alias,
      slug: !!slug ? slug : null,
    });

    const favicon = document.createElement('link');

    favicon.rel = 'icon';
    favicon.href = theme?.favicon_url || '/favicon.svg';

    document.head.appendChild(favicon);
    document.title = theme?.name || 'Soraban';

    setFirmTheme(theme);
  }, []);

  const orderedFirmGroups: Array<FirmGroup> = useMemo(() => {
    return (
      firm?.firm_groups?.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;

        return 0;
      }) || []
    );
  }, [firm?.firm_groups]);

  const entityTagOptions: Array<SelectItem> = useMemo(() => {
    const emptyEntityTag: SelectItem = {
      name: 'None',
      value: EMPTY_TAG_STRING,
    };

    // TODO: refactor all tag handling to use IDs instead of display names
    // e.g., `value` should be `entityTag.id.toString()` instead of `entityTag.name`
    const entityTags: Array<SelectItem> =
      firm?.entity_tags?.map((entityTag) => ({
        name: entityTag.name,
        value: entityTag.name,
      })) || [];
    const allEntityTags: Array<SelectItem> = [emptyEntityTag, ...entityTags];

    return allEntityTags;
  }, [firm?.entity_tags]);

  const entityTagOptionsToAdd: Array<ResultListItem> = useMemo(() => {
    const entityTags: Array<ResultListItem> =
      firm?.entity_tags?.map((entityTag) => ({
        name: entityTag.name,
        value: entityTag.name,
      })) || [];

    return entityTags;
  }, [firm?.entity_tags]);

  const clientTagOptions: Array<ResultListItem> = useMemo(() => {
    const emptyClientTag: ResultListItem = {
      name: 'None',
      value: EMPTY_TAG_STRING,
    };
    const clientTags: Array<ResultListItem> =
      firm?.contact_tags?.map((clientTag) => ({
        name: clientTag.name,
        value: clientTag.name,
      })) || [];
    const allClientTags: Array<ResultListItem> = [
      emptyClientTag,
      ...clientTags,
    ];

    return allClientTags;
  }, [firm?.contact_tags]);

  const clientTagOptionsToAdd = useMemo(() => {
    const clientTags: Array<ResultListItem> =
      firm?.contact_tags?.map((clientTag) => ({
        name: clientTag.name,
        value: clientTag.name,
      })) || [];

    return clientTags;
  }, [firm?.contact_tags]);

  const firmGroupQuestionnaireOptions: Array<SelectItem> = useMemo(() => {
    const emptyFirmGroup: SelectItem = { name: 'None', value: '-1' };
    const questionnaireFirmGroups: Array<SelectItem> = orderedFirmGroups.map(
      (fg) => ({
        name: fg.name,
        value: fg.id.toString(),
      }),
    );
    const possibleFirmGroups: Array<SelectItem> = [
      emptyFirmGroup,
      ...questionnaireFirmGroups,
    ];

    return possibleFirmGroups;
  }, [orderedFirmGroups]);

  const firmGroupOptions: Array<SelectItem> = useMemo(() => {
    const firmGroupId: number | undefined = user?.firm_group?.id;

    if (!firmPreferences.has_firm_groups || !firmGroupId) {
      return [
        ...orderedFirmGroups?.map((fg) => ({
          name: fg.name,
          value: fg.id.toString(),
        })),
      ];
    }

    return [
      ...orderedFirmGroups
        .filter((fg) => fg.id === firmGroupId)
        .map((fg) => ({
          name: fg.name,
          value: fg.id.toString(),
        })),
    ];
  }, [orderedFirmGroups, user, firmPreferences]);

  const firmUserOptions: Array<SelectItem> = useMemo(() => {
    const isFirmGroupUser: boolean = user?.firm_access === 'group';
    const firmGroupId: number | undefined = user?.firm_group?.id;

    if (isFirmGroupUser && firmPreferences.has_firm_groups && firmGroupId) {
      return firmUsers
        .filter(
          (fu) =>
            (fu.access === 'group' && fu.group?.id === firmGroupId) || // same group
            fu.access !== 'group',
        )
        .map((fu) => {
          const fullName: string = getFullName(fu);

          return {
            name: fullName,
            value: fu.id.toString(),
            firm_group_id: fu.group?.id,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    } else if (
      isFirmGroupUser &&
      !firmGroupId &&
      firmPreferences.has_firm_groups
    ) {
      return [];
    } else {
      return firmUsers
        ?.map((fu) => {
          const fullName: string = getFullName(fu);

          return {
            name: fullName,
            value: fu.id.toString(),
            firm_group_id: fu.group?.id,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [firmUsers, user, firmPreferences]);

  const firmRoleOptions: Array<SelectItem> = useMemo(
    () =>
      firmRoles?.map((fr) => {
        return {
          name: fr.role_name,
          value: fr.id.toString(),
        };
      }),
    [firmRoles],
  );

  const firmAccessOptions = useMemo(() => {
    const groupEnabled = firmPreferences?.has_firm_groups;
    if (groupEnabled) {
      return FIRM_ACCESS_OPTIONS;
    }
    return FIRM_ACCESS_OPTIONS.filter((option) => option.value !== 'group');
  }, [firmPreferences?.has_firm_groups]);

  const colorPrimary: string | undefined = useMemo(() => {
    let primaryColor: string | undefined =
      firmTheme?.theme?.primary || firmPreferences.theme?.primary;
    const isValid: boolean = tinycolor(primaryColor).isValid();
    const isPrimaryDarkEnough: boolean = tinycolor(primaryColor).isDark();

    if (!isValid) return undefined;
    if (!isPrimaryDarkEnough) return undefined;

    const primaryLuminance = tinycolor(primaryColor).getLuminance();
    const primaryBrightness = tinycolor(primaryColor).getBrightness();

    if (primaryBrightness > 200) {
      primaryColor = tinycolor(primaryColor).darken(100).toHex();
    }

    if (primaryLuminance > 0.75) {
      primaryColor = tinycolor(primaryColor).desaturate(25).toHex();
    }

    return primaryColor;
  }, [firmPreferences, firmTheme?.theme?.primary]);

  const colorSecondary: string | undefined = useMemo(() => {
    const secondaryColor: string | undefined =
      firmTheme?.theme?.secondary ||
      firmPreferences.theme?.secondary ||
      firmPreferences.theme?.accent;

    return secondaryColor;
  }, [firmPreferences, firmTheme?.theme?.secondary]);

  const firmLogoUrl: string | null | undefined = useMemo(() => {
    return firmTheme?.url || firm?.medium_url;
  }, [firmTheme?.url, firm?.medium_url]);

  const hasCustomPermissionLevels: boolean = useMemo(() => {
    // Use data from firm instead of separate state
    return firm?.permission_levels_config?.has_custom_levels || false;
  }, [firm?.permission_levels_config?.has_custom_levels]);

  // Use permission levels config from firm data
  const firmPermissionLevelsConfig = useMemo(() => {
    return firm?.permission_levels_config;
  }, [firm?.permission_levels_config]);

  const updateFirmPreference = async (
    preferenceKey: string,
    preferenceValue: any,
  ) => {
    setLoading(true);

    const newPreferences: FirmPreferences = {
      ...firmPreferences,
      [preferenceKey]: preferenceValue,
    };

    try {
      const { error } = await updatePreferences({
        token,
        preferences: newPreferences,
      });

      if (!error) {
        addToast('success', 'Updated successfully');
      }
    } catch (error) {
      addToast('error', 'Failed to update', { error });
    } finally {
      setLoading(false);
      reloadFirm();
    }
  };

  const updateFirmPreferences = async (
    incomingPreferences: Partial<FirmPreferences>,
  ) => {
    setLoading(true);

    const newPreferences: FirmPreferences = {
      ...firmPreferences,
      ...incomingPreferences,
    };

    try {
      const { error } = await updatePreferences({
        token,
        preferences: newPreferences,
      });

      if (!error) {
        addToast('success', 'Updated preferences successfully');
      }
    } catch (error) {
      addToast('error', 'Failed to update', { error });
    } finally {
      setLoading(false);
      reloadFirm();
    }
  };

  const getFirmEntityTypes = useCallback(async () => {
    if (!token || !isAdvisor) return;

    const { entity_types, error } = await getAllEntityTypes({ token });

    if (!error && Array.isArray(entity_types)) {
      setFirmEntityTypes(entity_types);
    }
  }, [token, isAdvisor]);

  const getFirmLinkedAccounts = useCallback(async () => {
    if (!token || !isAdvisor) return;

    const { accounts, error } = await getLinkedAccounts({ token });

    if (!error && Array.isArray(accounts)) {
      setFirmLinkedAccounts(accounts);
    }
  }, [token, isAdvisor]);

  const handleInitializeCustomPermissionLevels = useCallback(async () => {
    if (!token || !isAdvisor) return;

    try {
      setLoadingPermissionLevels(true);
      const response = await initializeCustomPermissionLevels({ token });

      if (!response?.error) {
        addToast(
          'success',
          'Custom permission levels initialized successfully',
        );
        await reloadFirm();
      }
    } catch (error) {
      addToast('error', 'Failed to initialize custom permission levels');
    } finally {
      setLoadingPermissionLevels(false);
    }
  }, [token, isAdvisor, addToast, reloadFirm]);

  const handleResetPermissionLevelsToDefaults = useCallback(async () => {
    if (!token || !isAdvisor) return;

    try {
      setLoadingPermissionLevels(true);
      const response = await resetPermissionLevelsToDefaults({ token });

      if (!response?.error) {
        addToast('success', 'Permission levels reset to defaults successfully');
        await reloadFirm();
      }
    } catch (error) {
      console.error('Failed to reset permission levels:', error);
      addToast('error', 'Failed to reset permission levels to defaults');
    } finally {
      setLoadingPermissionLevels(false);
    }
  }, [token, isAdvisor, addToast, reloadFirm]);

  const handleUpdateCustomPermissionLevel = useCallback(
    async (backendName: string, permissionIds: Array<PermissionsMapEnum>) => {
      if (!token || !isAdvisor) return;

      try {
        await updateCustomPermissionLevel({
          token,
          backend_name: backendName,
          permission_ids: permissionIds,
        });
      } catch (error) {
        addToast('error', 'Failed to update permission level', { error });
      } finally {
        await reloadFirm();
      }
    },
    [token, isAdvisor, addToast, reloadFirm],
  );

  useEffect(() => {
    if (token && pathname === '/settings') {
      getFirmLinkedAccounts();
    }
  }, [token, pathname, getFirmLinkedAccounts]);

  useEffect(() => {
    if (firm?.id && !!token && isAdvisor) {
      const _loadFirmCustomStatuses = async () => {
        if (!isAdvisor || !token) return;
        const statuses = await getFirmCustomStatuses({
          firmId: firm?.id!,
          token,
        });

        setFirmCustomStatuses(statuses);
      };

      _loadFirmCustomStatuses();
    }
  }, [firm?.id, isAdvisor, token]);

  useEffect(() => {
    const loadFirmData = async () => {
      if (token) {
        await reloadFirm();
      }
      await loadFirmTheme();
    };

    loadFirmData();
  }, [token, isAdvisor, reloadFirm, loadFirmTheme]);

  useEffect(() => {
    getFirmEntityTypes();
  }, [getFirmEntityTypes]);

  const loadDeliveryMessageTemplates = useCallback(async () => {
    if (!token || !isAdvisor) return;

    setLoadingDeliveryMessageTemplates(true);
    try {
      const templates = await getDeliveryMessageTemplates({ token });
      setDeliveryMessageTemplates(templates || []);
    } catch (error) {
      console.error('Error fetching delivery message templates:', error);
    } finally {
      setLoadingDeliveryMessageTemplates(false);
    }
  }, [token, isAdvisor]);

  useEffect(() => {
    loadDeliveryMessageTemplates();
  }, [loadDeliveryMessageTemplates]);

  return (
    <FirmSettingsContext
      value={{
        availableFirms,
        clientTagOptions,
        clientTagOptionsToAdd,
        colorPrimary,
        colorSecondary,
        deliveryMessageTemplates,
        entityTagOptions,
        entityTagOptionsToAdd,
        firm,
        firmCustomStatuses,
        firmEntityTypes,
        firmGroupOptions,
        firmGroupQuestionnaireOptions,
        firmGroups: orderedFirmGroups,
        firmLinkedAccounts,
        firmLogoUrl,
        firmRoleOptions,
        firmRoles,
        firmTheme,
        firmUserOptions,
        firmUsers,
        glossaries,
        hasCustomPermissionLevels,
        initializeCustomPermissionLevels:
          handleInitializeCustomPermissionLevels,
        isLoading,
        isLoadingDeliveryMessageTemplates,
        isLoadingFirmPreferences,
        isLoadingPermissionLevels,
        isStripeConnected,
        permissionLevelsConfig: firmPermissionLevelsConfig,
        preferences: firmPreferences,
        reloadDeliveryMessageTemplates: loadDeliveryMessageTemplates,
        reloadFirm,
        reloadFirmRoles: loadFirmRoles,
        reloadFirmUsers: loadAdvisors,
        resetPermissionLevelsToDefaults: handleResetPermissionLevelsToDefaults,
        setFirm,
        setFirmTheme,
        updateCustomPermissionLevel: handleUpdateCustomPermissionLevel,
        updateFirmPreference,
        updateFirmPreferences,
        firmAccessOptions,
      }}
    >
      {children}
    </FirmSettingsContext>
  );
};

export { FirmSettingsContext, FirmSettingsProvider };
